import { useState, useEffect } from "react"
import { BookmarkCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { WinThemeEvidenceList } from "@/components/strategy/WinThemeEvidenceList"
import { AddEvidencePopover } from "@/components/strategy/AddEvidencePopover"
import { useStrategySettingsStore } from "@/stores/strategy-settings-store"
import type { WinTheme, ThemeCategory, ThemeStrength, ThemeEvidence } from "@/stores/strategy-store"
import type { ExtractedCriterion } from "@/types/extraction"

interface WinThemeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  theme?: WinTheme | null // null = create, defined = edit
  /** Pre-populate fields without putting the dialog in edit mode (used when editing a pending AI suggestion before acceptance). */
  prefill?: {
    title: string
    description: string | null
    category: ThemeCategory
    strength: ThemeStrength
    evidence: ThemeEvidence[]
    evaluationCriteriaIds: string[]
  } | undefined
  /** Pre-select evaluation criteria when opening a fresh create dialog (e.g. from a gap chip). Ignored if `prefill` or `theme` is provided. */
  prefilledCriteriaIds?: string[]
  evalCriteria: ExtractedCriterion[]
  onConfirm: (data: {
    title: string
    description: string | null
    category: ThemeCategory
    strength: ThemeStrength
    evidence: ThemeEvidence[]
    evaluationCriteriaIds: string[]
  }) => void | Promise<void>
  isLoading?: boolean
}

const categories: { value: ThemeCategory; label: string }[] = [
  { value: 'experience', label: 'Experience' },
  { value: 'personnel', label: 'Personnel' },
  { value: 'methodology', label: 'Methodology' },
  { value: 'innovation', label: 'Innovation' },
  { value: 'relationship', label: 'Relationship' },
  { value: 'value', label: 'Value' },
  { value: 'risk', label: 'Risk Mitigation' },
  { value: 'local_presence', label: 'Local Presence' },
  { value: 'other', label: 'Other' },
]

const strengths: { value: ThemeStrength; label: string }[] = [
  { value: 'strong', label: 'Strong' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'weak', label: 'Weak' },
]

function formatWeight(ec: ExtractedCriterion): string {
  if (ec.weightPercentage != null) return `(${ec.weightPercentage}%)`
  if (ec.weightPoints != null) return `(${ec.weightPoints} pts)`
  return ''
}

export function WinThemeDialog({ open, onOpenChange, theme, prefill, prefilledCriteriaIds, evalCriteria, onConfirm, isLoading }: WinThemeDialogProps) {
  const isEdit = theme != null
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<ThemeCategory>('experience')
  const [strength, setStrength] = useState<ThemeStrength>('moderate')
  const [evidence, setEvidence] = useState<ThemeEvidence[]>([])
  const [selectedCriteriaIds, setSelectedCriteriaIds] = useState<string[]>([])
  const [bankThemeId, setBankThemeId] = useState<string>('')

  const bankThemes = useStrategySettingsStore(s => s.bankThemes)
  const loadBankThemes = useStrategySettingsStore(s => s.loadBankThemes)

  // Lazy-load library themes the first time the create dialog opens
  useEffect(() => {
    if (open && !isEdit && bankThemes.length === 0) {
      void loadBankThemes()
    }
  }, [open, isEdit, bankThemes.length, loadBankThemes])

  // Populate form when editing OR when a prefill (e.g. pending AI suggestion) is supplied
  useEffect(() => {
    if (theme) {
      setTitle(theme.title)
      setDescription(theme.description ?? '')
      setCategory(theme.category)
      setStrength(theme.strength)
      setEvidence([...theme.evidence])
      setSelectedCriteriaIds([...theme.evaluationCriteriaIds])
      setBankThemeId('')
    } else if (prefill) {
      setTitle(prefill.title)
      setDescription(prefill.description ?? '')
      setCategory(prefill.category)
      setStrength(prefill.strength)
      setEvidence([...prefill.evidence])
      setSelectedCriteriaIds([...prefill.evaluationCriteriaIds])
      setBankThemeId('')
    } else {
      setTitle('')
      setDescription('')
      setCategory('experience')
      setStrength('moderate')
      setEvidence([])
      setSelectedCriteriaIds(prefilledCriteriaIds ?? [])
      setBankThemeId('')
    }
  }, [theme, prefill, prefilledCriteriaIds, open])

  const handleSelectBankTheme = (id: string) => {
    setBankThemeId(id)
    const picked = bankThemes.find(t => t.id === id)
    if (!picked) return
    setTitle(picked.title)
    setDescription(picked.description ?? '')
    setCategory(picked.category)
  }

  const handleConfirm = () => {
    if (!title.trim()) return
    void onConfirm({
      title: title.trim(),
      description: description.trim() || null,
      category,
      strength,
      evidence,
      evaluationCriteriaIds: selectedCriteriaIds,
    })
  }

  const toggleCriterion = (id: string) => {
    setSelectedCriteriaIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  // Only show top-level criteria (not sub-criteria)
  const topLevelCriteria = evalCriteria.filter(ec => ec.id)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader className="min-w-0">
          <DialogTitle>{isEdit ? 'Edit Win Theme' : 'Add Win Theme'}</DialogTitle>
          <DialogDescription>Define a key reason the client should choose you.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 min-w-0">
          {/* Library picker — create mode only */}
          {!isEdit && (
            <div className="bg-muted/30 border border-border rounded-lg p-3">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-1.5">
                <BookmarkCheck className="h-3.5 w-3.5" />
                Use template from library
                <span className="font-normal">(optional)</span>
              </label>
              {bankThemes.length > 0 ? (
                <Select value={bankThemeId} onValueChange={handleSelectBankTheme}>
                  <SelectTrigger className="bg-card">
                    <SelectValue placeholder="Start from a saved theme..." />
                  </SelectTrigger>
                  <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                    {bankThemes.map(t => (
                      <SelectItem key={t.id} value={t.id} className="pr-2">
                        <span className="block truncate" title={t.title}>{t.title}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-meta">
                  No saved templates yet. Save themes to your library from any bid or in Settings &rsaquo; Bid Strategy to reuse them here.
                </p>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="text-sm font-medium block mb-1.5">Theme title</label>
            <Input
              placeholder="e.g., Proven Track Record in Bridge Rehabilitation"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Category + Strength */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1.5">Category</label>
              <Select value={category} onValueChange={(v) => setCategory(v as ThemeCategory)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Strength</label>
              <Select value={strength} onValueChange={(v) => setStrength(v as ThemeStrength)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {strengths.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium block mb-1.5">Description</label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-transparent resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              rows={3}
              placeholder="Explain this theme — what makes it compelling?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Evidence */}
          <div>
            <label className="text-sm font-medium block mb-2">Evidence</label>
            {evidence.length > 0 && (
              <div className="mb-2">
                <WinThemeEvidenceList
                  evidence={evidence}
                  onRemove={(i) => setEvidence(prev => prev.filter((_, idx) => idx !== i))}
                />
              </div>
            )}
            <AddEvidencePopover onAdd={(e) => setEvidence(prev => [...prev, e])} />
          </div>

          {/* Map to evaluation criteria */}
          {topLevelCriteria.length > 0 && (
            <div>
              <label className="text-sm font-medium block mb-2">Map to evaluation criteria</label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {topLevelCriteria.map(ec => (
                  <label key={ec.id} className="flex items-center gap-2.5 cursor-pointer">
                    <Checkbox
                      checked={selectedCriteriaIds.includes(ec.id!)}
                      onCheckedChange={() => toggleCriterion(ec.id!)}
                    />
                    <span className="text-sm">
                      {ec.title} <span className="text-muted-foreground">{formatWeight(ec)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {topLevelCriteria.length === 0 && (
            <p className="text-meta">
              Run tender analysis (Evaluation Criteria) to map themes to criteria.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={!title.trim() || isLoading}>
            {isLoading ? 'Saving...' : isEdit ? 'Save Theme' : 'Add Theme'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
